import { Box, CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';

interface ScrollAreaProps {
    children: React.ReactNode;
    hasMore: boolean;
    loading: boolean;
    onLoadMore: () => void;
    sx?: any;
    orientation?: 'vertical' | 'horizontal';
}

const ScrollArea: React.FC<ScrollAreaProps> = ({
    children,
    hasMore,
    loading,
    onLoadMore,
    sx,
    orientation = 'vertical'
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const loaderRef = useRef<HTMLDivElement>(null);
    const loadingRef = useRef(false);

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || loading || !hasMore || loadingRef.current) return;

        const container = scrollContainerRef.current;

        let scrollPercentage: number;

        if (orientation === 'horizontal') {
            const { scrollLeft, scrollWidth, clientWidth } = container;

            // Only trigger if there's actually scrollable content
            if (scrollWidth <= clientWidth) return;

            scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;
        } else {
            const { scrollTop, scrollHeight, clientHeight } = container;

            // Only trigger if there's actually scrollable content
            if (scrollHeight <= clientHeight) return;

            scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
        }

        // Trigger load more when scrolled to 80% of the content
        if (scrollPercentage > 0.8) {
            loadingRef.current = true;
            onLoadMore();
        }
    }, [loading, hasMore, onLoadMore, orientation]);

    // Reset loading ref when loading state changes
    useEffect(() => {
        if (!loading) {
            loadingRef.current = false;
        }
    }, [loading]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.addEventListener('scroll', handleScroll);

        // Check if we need to load more on initial render (when content doesn't fill viewport)
        const checkInitialLoad = () => {
            if (!hasMore || loading || loadingRef.current) return;

            if (orientation === 'horizontal') {
                const { scrollWidth, clientWidth } = container;
                // If content doesn't fill the viewport and we have more items, load them
                if (scrollWidth <= clientWidth) {
                    loadingRef.current = true;
                    onLoadMore();
                }
            } else {
                const { scrollHeight, clientHeight } = container;
                // If content doesn't fill the viewport and we have more items, load them
                if (scrollHeight <= clientHeight) {
                    loadingRef.current = true;
                    onLoadMore();
                }
            }
        };

        // Small delay to ensure content is rendered
        const timer = setTimeout(checkInitialLoad, 100);

        return () => {
            container.removeEventListener('scroll', handleScroll);
            clearTimeout(timer);
        };
    }, [handleScroll, hasMore, loading, onLoadMore, orientation]);

    return (
        <Box
            ref={scrollContainerRef}
            sx={{
                flex: 1,
                overflow: 'auto',
                display: orientation === 'horizontal' ? 'flex' : 'block',
                flexDirection: orientation === 'horizontal' ? 'row' : undefined,
                ...sx,
            }}>
            {children}
            {hasMore && (
                <Box
                    ref={loaderRef}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={orientation === 'vertical' ? 2 : 0}
                    px={orientation === 'horizontal' ? 2 : 0}
                    width={orientation === 'vertical' ? '100%' : 'auto'}
                    height={orientation === 'horizontal' ? '100%' : 'auto'}
                    minWidth={orientation === 'horizontal' ? '60px' : undefined}
                >
                    <CircularProgress size={24} />
                </Box>
            )}
        </Box>
    );
};

export default ScrollArea;
